import React, { useMemo } from 'react';
import { toast } from 'react-toastify';

import { useMutation } from '@apollo/client';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';

import { IBaseDrawerProps, IBusinessPartnerDetails } from '@common/types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { useSelector } from 'react-redux';
import { RootState } from '@core/redux/store';
import {
  useGetBusinessPartnerLovs,
  useModifyBusinessPartner
} from '@common/slices';
import { modifyBusinessPartnerDrawerSections } from '@common/constants';
import { IAbstractRecord } from '@models';
import Loader from '@components/loader/Loader';

function toLookups(data: any) {
  const personTypes: Record<string, string> = {
    INDIVIDUAL: 'Individual',
    COMPANY: 'Company'
  };

  const businessPartnerTypes: Record<string, string> = {};
  data?.BusinessPartnerType?.enumValues?.forEach((el: any) => {
    businessPartnerTypes[el.name] = el.name
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/^\w/, (c: any) => c.toUpperCase());
  });

  const addressTypes: Record<string, string> = {
    WORK: 'Work',
    HOME: 'Home'
  };

  const personTitles: Record<string, string> = {};
  data?.core?.getTitles?.forEach((el: any) => {
    personTitles[el.id] = el.title;
  });

  const relatedCompanies: Record<string, string> = {};
  data?.salesforce?.getRelatedCompanies?.forEach((el: any) => {
    relatedCompanies[el.id] = el.title;
  });

  const activePersons: Record<string, string> = {};
  data?.salesforce?.getActivePersons?.forEach((el: any) => {
    activePersons[el.id] = el.fullName;
  });

  const countries: Record<string, string> = {};
  data?.core?.getCountries?.forEach((el: any) => {
    countries[el.id] = el.title;
  });

  const zones: Record<string, string> = {};
  data?.core?.getZones?.forEach((el: any) => {
    zones[el.id] = el.title;
  });

  const currencies: Record<string, string> = {};
  data?.core?.getCurrencies?.forEach((el: any) => {
    currencies[el.id] = el.title;
  });

  return {
    personTypes,
    businessPartnerTypes,
    addressTypes,
    personTitles,
    relatedCompanies,
    activePersons,
    countries,
    zones,
    currencies
  };
}

interface Props extends IBaseDrawerProps {
  businessPartnerId: string;
  businessPartnerDetails: IBusinessPartnerDetails;
}

const ModifyBusinessPartnerDrawer: React.FC<Props> = ({
  onSuccess,
  businessPartnerId,
  businessPartnerDetails
}) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );

  const { data, loading } = useGetBusinessPartnerLovs.useQuery();

  const [modifyBusinessPartner] = useModifyBusinessPartner(businessPartnerId);

  const lovs = useMemo<Record<string, Record<string, string>>>(() => {
    if (!data) return {};

    return toLookups({
      salesforce: {
        getActivePersons: data.salesforce?.getActivePersons,
        getRelatedCompanies: data.salesforce?.getRelatedCompanies
      },
      core: {
        getTitles: data.core?.getTitles,
        getCountries: data.core?.getCountries,
        getZones: data.core?.getZones,
        getCurrencies: data.core?.getCurrencies
      },
      BusinessPartnerType: data?.BusinessPartnerType
    });
  }, [data]);

  const sections = useMemo<IDynamicFormWidgetSection[]>(() => {
    if (!data) return [];
    const bp = businessPartnerDetails;
    const formattedValues: IAbstractRecord = {
      type: bp?.person?.type,
      title: bp?.person?.title,
      firstName: bp?.person?.firstName,
      middleName: bp?.person?.middleName,
      lastName: bp?.person?.lastName,
      dateOfBirth: bp?.person?.dateOfBirth,
      companyName: bp?.fullName,
      email: bp?.person?.email,
      mobileNumber: bp?.person?.phoneDetails?.phoneNumber,
      relatedCompany: bp?.relatedCompany,
      bpType: bp?.type,
      isCompany: bp?.isCompany,
      isLocal: bp?.isLocal,
      isRegistered: bp?.isRegistered,
      registerNumber: bp?.registerationNumber,
      registrationExpiryDate: bp?.registerationExpiryDate,
      taxAuxiliary: bp?.taxAuxiliaryAccount,
      auxiliary: bp?.auxiliaryAccount,
      allowAccessToAllInsured: bp?.hasAccessToAllInsured
    };

    return modifyBusinessPartnerDrawerSections(formattedValues, lovs);
  }, [lovs, data]);

  const onSubmit = async (formData: any) => {
    try {
      const payload = {
        id: businessPartnerId,
        taxAuxiliary: formData.taxAuxiliary || null,
        relatedCompany: formData.relatedCompany || null,
        registrationNumber: formData.registerNumber || null,
        registrationExpiryDate: formData.registrationExpiryDate || null,
        isRegistered: formData.isRegistered,
        isLocal: formData.isLocal,
        isCompany: formData.isCompany,
        businessPartnerType: formData.bpType || null,
        auxiliary: formData.auxiliary || null,
        allowAccessToAllInsured: formData.allowAccessToAllInsured
      };

      const res = await modifyBusinessPartner({
        modifyBusinessPartnerInput: payload
      });

      if (!res.errors) {
        onSuccess();
      }
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  if (loading) return <Loader />;

  return (
    <DynamicFormWidget
      submitButtonState={submitButtonState}
      sections={sections}
      onSubmit={onSubmit}
    />
  );
};

export { ModifyBusinessPartnerDrawer };
